import React, { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import api from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, X, AlertTriangle, Clock } from "lucide-react";
import { links } from "./ClinicAdminPages";

export function ClinicBillingPage() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [clinicId, setClinicId] = useState(null);
  const [confirming, setConfirming] = useState(null); // plan object being confirmed
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    const me = await api.get("/auth/me");
    const cid = me.data.clinic_id;
    setClinicId(cid);
    const [sub, ps] = await Promise.all([
      api.get(`/subscriptions/clinic/${cid}`),
      api.get("/subscriptions/plans"),
    ]);
    setData(sub.data);
    setPlans(ps.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openConfirm = (plan) => { setConfirming(plan); setReason(""); };
  const cancelPlanRequest = async () => {
    setSaving(true);
    try { await api.post(`/subscriptions/clinic/${clinicId}/request/cancel`, { kind: "plan", reason: "Cancelled by clinic administrator", request_id: data.pending_plan_request.request_id }); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unable to cancel plan request"); }
    setSaving(false);
  };

  const submitUpgrade = async () => {
    if (!reason.trim()) { toast.error("Please add a short reason (e.g. Need more staff seats)"); return; }
    setSaving(true);
    try {
      await api.post(`/subscriptions/clinic/${clinicId}/change-plan`, { plan_id: confirming.plan_id, reason });
      toast.success(`Request submitted for ${confirming.name}. MediGenie will review billing and activation.`);
      setConfirming(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update plan");
    }
    setSaving(false);
  };

  if (!data) {
    return <DashboardLayout links={links} title="Billing & Plan"><div className="mg-card text-sm text-mg-muted">Loading…</div></DashboardLayout>;
  }

  const { subscription: sub, plan, effective_features: feats, trial_remaining_days } = data;
  const enabledFeats = Object.entries(feats || {}).filter(([, v]) => v.enabled);
  const status = data.access?.status || sub?.status || "active";

  return (
    <DashboardLayout links={links} title="Billing & Plan">
      {data.pending_plan_request && <div className="mg-card mb-5" role="status">Your request for {data.pending_plan_request.plan_name} is awaiting MediGenie review. Your current plan remains in effect until approval.<button className="mg-btn-secondary ml-3" disabled={saving} onClick={cancelPlanRequest}>Cancel plan request</button></div>}
      {data.quotas && <div className="mg-card mb-5"><h3 className="font-bold mb-3">Plan allowances for this branch</h3><ul className="space-y-2 text-sm">{Object.entries(data.quotas).map(([key, quota]) => <li key={key}>{key.replace(/_/g, " ")}: {quota.used} used / {quota.limit ?? "unlimited"}{quota.over_limit ? " — existing records retained; new additions paused" : ""}</li>)}</ul><p className="text-xs mt-3">Monthly booking usage counts new bookings in the clinic’s calendar month. Cancelling a booking does not reset usage.</p></div>}
      <div className="mg-card mb-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Current plan</div>
            <h2 className="text-2xl font-extrabold text-mg-navy" data-testid="current-plan-name">{plan?.name || "No plan"}</h2>
            <p className="text-xs text-slate-500 mt-1">{plan?.short_description}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className={`mg-status mg-status-${status === "active" ? "active" : status === "trial" ? "checked-in" : "cancelled"}`} data-testid="billing-sub-status">
                {status.toUpperCase()}
              </span>
              {sub?.is_trial && (
                <span className="bg-green-50 text-mg-health text-xs font-bold rounded-full px-2.5 py-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Trial · {trial_remaining_days ?? "?"} day(s) left
                </span>
              )}
              {plan?.monthly_price ? <span className="text-xs text-slate-500">₹{Number(plan.monthly_price).toLocaleString()}/mo</span> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="mg-card mb-5">
        <h3 className="font-bold text-mg-navy text-sm mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-mg-medical" /> Features included ({enabledFeats.length})</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto text-xs">
          {enabledFeats.map(([k]) => (
            <div key={k} className="px-2 py-1 bg-slate-50 rounded text-mg-navy">{k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</div>
          ))}
        </div>
      </div>

      <h3 className="font-bold text-mg-navy mb-3">Available Plans</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const isCurrent = p.plan_id === plan?.plan_id;
          return (
            <div key={p.plan_id} className={`mg-card ${p.is_recommended ? "border-2 border-mg-primary" : ""}`} data-testid={`plan-card-${p.plan_id}`}>
              {p.is_recommended && <div className="text-[10px] font-bold text-mg-primary uppercase tracking-wider mb-1">Recommended</div>}
              <h4 className="text-xl font-extrabold text-mg-navy">{p.name}</h4>
              <p className="text-xs text-slate-500 mt-1 mb-3">{p.short_description}</p>
              <div className="text-2xl font-extrabold text-mg-navy mb-4">₹{Number(p.monthly_price).toLocaleString()}<span className="text-sm font-normal text-slate-400">/mo</span></div>
              <button
                onClick={() => openConfirm(p)}
                disabled={isCurrent}
                className={`w-full text-sm font-semibold py-2.5 rounded-xl transition ${isCurrent ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "mg-btn-primary justify-center"}`}
                data-testid={`select-plan-${p.plan_id}`}
              >
                {isCurrent ? "Current Plan" : <><Sparkles className="w-4 h-4 inline mr-1" /> Request {p.name}</>}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-4">Need a custom Enterprise plan or have questions? Contact your MediGenie account manager.</p>

      {confirming && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setConfirming(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="confirm-upgrade-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-mg-navy">Request {confirming.name}?</h3>
              <button onClick={() => setConfirming(null)}><X className="w-4 h-4 text-mg-muted" /></button>
            </div>
            <div className="space-y-3">
              {(() => {
                const lost = Object.entries(plan?.features || {}).filter(([k, v]) => v.enabled && !confirming.features?.[k]?.enabled);
                if (lost.length === 0) return null;
                return (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <div>
                      <div className="font-bold text-amber-800">This plan will disable:</div>
                      <div className="mt-1 text-amber-700">{lost.map(([k]) => k.replace(/_/g, " ")).join(", ")}</div>
                    </div>
                  </div>
                );
              })()}
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Reason for change</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} className="mg-input mt-1 w-full" placeholder="e.g. Need more staff seats" data-testid="upgrade-reason-input" />
                {!reason.trim() && <span className="text-[11px] text-red-500 mt-1 block">Reason is required to confirm the switch</span>}
              </label>
              <p className="text-xs text-slate-400">Requested price: ₹{Number(confirming.monthly_price).toLocaleString()}/mo. Our billing team will confirm payment terms and activation. Submitting this request does not change your plan or charge you.</p>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setConfirming(null)} className="mg-btn-secondary text-sm">Cancel</button>
                <button onClick={submitUpgrade} disabled={saving || !reason.trim()} className="mg-btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed" data-testid="confirm-upgrade-btn">{saving ? "Saving…" : "Submit Request"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
